"use client";
import React from 'react';
import clsx from 'clsx';
import { handleEnterpriseForm } from './actions';
import { useActionState } from 'react'; // Updated hook import
import ClientFormHelper from '@/app/lib/ClientFormHelper.client';
import { AdbeatButton } from '../components/adbeatButton';
import TurnstileWidget, { type TurnstileWidgetHandle } from '@/app/components/TurnstileWidget.client';

const labelClassName = clsx(
  "text-[#a3b3bf]",
  "uppercase",
  "text-[12px]",
);

const inputClassName = clsx(
  "p-[15px]",
  "border",
  "border-[#dfe2e6]",
  "rounded",
  "w-full",
  "text-[#999]",
);

const requiredClassName = clsx(
  "text-[red]",
  "px-[5px]",
);

const initialState = {
  error: {},
  email: "",
  firstname: "",
  lastname: "",
  phone: "",
  company: "",
  title: "",
  btype: "",
  budget: "",
  decision: "",
  captchaVal: "",
  newsletter: 0,
};

type EnterpriseFormProps = {
  formName: string;
  captchaEnabled: boolean;
};

const EnterpriseForm = (props: EnterpriseFormProps) => {
  const [state, formAction, isPending] = useActionState(handleEnterpriseForm, initialState); // Updated hook usage
  const formHelper = new ClientFormHelper(state);
  const [turnstileToken, setTurnstileToken] = React.useState('');
  const [turnstileError, setTurnstileError] = React.useState('');
  const turnstileRef = React.useRef<TurnstileWidgetHandle>(null);

  // Turnstile tokens are single-use. After any failed submission, reset the
  // widget so the user can re-solve before resubmitting.
  React.useEffect(() => {
    if (state.error && Object.keys(state.error).length > 0) {
      turnstileRef.current?.reset();
      setTurnstileToken('');
    }
  }, [state]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (props.captchaEnabled && !turnstileToken) {
      event.preventDefault();
      setTurnstileError('Please complete the captcha verification before submitting.');
    }
  };

  return (
    <div className="">
      <form id={props.formName} acceptCharset="UTF-8" action={formAction} onSubmit={onSubmit} className="hs-form stacked hs-form-private">
        <div
          className={clsx(
            formHelper.hasErrorFeedback('firstname')
          )}
        >
          <label className={labelClassName} htmlFor="firstname">
            <span>First Name</span>
            <span className={requiredClassName}>*</span>
          </label>
          <div className="input">
            <input className={inputClassName} type="text" name="firstname" required={true} defaultValue={state.firstname}
              placeholder="Enter your First Name" />
            {formHelper.hasErrorDetails('firstname')}
          </div>
        </div>
        <div className={`pt-[10px] ${formHelper.hasErrorFeedback('lastname')}`}>
          <label className={labelClassName} htmlFor="lastname">
            <span>Last Name</span>
            <span className={requiredClassName}>*</span>
          </label>
          <div className="input">
            <input id="lastname" className={inputClassName} type="text" name="lastname" required={true} defaultValue={state.lastname}
              placeholder="Enter your Last Name" />
            {formHelper.hasErrorDetails('lastname')}
          </div>
        </div>
        <div className={`pt-[10px] ${formHelper.hasErrorFeedback('email')}`}>
          <label id="label-email" className={labelClassName} htmlFor="email">
            <span>Email</span>
            <span className={requiredClassName}>*</span>
          </label>
          <div className="input">
            <input
              id="email"
              className={inputClassName}
              type="email"
              name="email"
              required={true}
              placeholder="Enter your Email"
              defaultValue={state.email}
            />
            {formHelper.hasErrorDetails('email')}
          </div>
        </div>
        <div className={`pt-[10px] ${formHelper.hasErrorFeedback('phone')}`}>
          <label id="label-phone" className={labelClassName} htmlFor="phone">
            <span>Phone Number</span>
            <span className={requiredClassName}>*</span>
          </label>
          <div className="input">
            <input
              id="phone"
              className={inputClassName}
              type="tel"
              name="phone"
              required={true}
              defaultValue={state.phone}
              placeholder="Enter your Phone Number"
            />
            {formHelper.hasErrorDetails('phone')}
          </div>
        </div>
        <div className={`pt-[10px] ${formHelper.hasErrorFeedback('company')}`}>
          <label id="label-company" className={labelClassName} htmlFor="company">
            <span>Company</span>
            <span className={requiredClassName}>*</span>
          </label>
          <div className="input">
            <input
              id="company"
              className={inputClassName}
              type="text"
              name="company"
              required={true}
              defaultValue={state.company}
              placeholder="Enter your Company"
            />
            {formHelper.hasErrorDetails('company')}
          </div>
        </div>
        <div className={`pt-[10px] ${formHelper.hasErrorFeedback('title')}`}>
          <label id="label-title" className={labelClassName} htmlFor="title">
            <span>Title</span>
            <span className={requiredClassName}>*</span>
          </label>
          <div className="input">
            <input
              id="title"
              className={inputClassName}
              type="text"
              name="title"
              required={true}
              defaultValue={state.title}
              placeholder="Enter your Title"
            />
            {formHelper.hasErrorDetails('title')}
          </div>
        </div>
        <div className={`pt-[10px] ${formHelper.hasErrorFeedback('business_type')}`}>
          <label id="label-business_type" className={labelClassName} htmlFor="business_type">
            <span>Business Type</span>
            <span className={requiredClassName}>*</span>
          </label>
          <div className="input">
            <select
              id="business_type"
              required={true}
              defaultValue={state.btype}
              className={clsx(inputClassName, "text-[#999]")}
              name="business_type"
            >
              <option value="" disabled={true}> Please Select -</option>
              <option value="Advertiser">Advertiser</option>
              <option value="Agency">Agency</option>
              <option value="Publisher/Ad Network/DSP">Publisher/Ad Network/DSP</option>
              <option value="SEM Manager/Analyst">SEM Manager/Analyst</option>
              <option value="Affiliate Marketer">Affiliate Marketer</option>
            </select>
            {formHelper.hasErrorDetails('business_type')}
          </div>
        </div>
        <div className={`pt-[10px] ${formHelper.hasErrorFeedback('budget')}`}>
          <label id="label-budget" className={labelClassName} htmlFor="budget">
            <span>Monthly Display/Native Ad Budget</span>
            <span className={requiredClassName}>*</span>
          </label>
          <div className="input">
            <select
              id="budget"
              required={true}
              defaultValue={state.budget}
              className={clsx(inputClassName, "text-[#999]")}
              name="budget"
            >
              <option value="" disabled={true}> Please Select -</option>
              <option value="Less than $25K">Less than $25K</option>
              <option value="$25K-$50K">$25K-$50K</option>
              <option value="$50K-$100K">$50K-$100K</option>
              <option value="$100K-$200K">$100K-$200K</option>
              <option value="$200K and above">$200K and above</option>
            </select>
            {formHelper.hasErrorDetails('budget')}
          </div>
        </div>
        <div className={`pt-[10px] ${formHelper.hasErrorFeedback('decision')}`}>
          <label id="label-decision" className={labelClassName} htmlFor="decision">
            <span>I&apos;m making a Purchase Decision in...</span>
            <span className={requiredClassName}>*</span>
          </label>
          <div className="input">
            <select
              id="decision"
              required={true}
              defaultValue={state.decision}
              className={clsx(inputClassName, "text-[#999]")}
              name="decision"
            >
              <option value="" disabled={true}>- Please Select -</option>
              <option value="0-3 Months">0-3 Months</option>
              <option value="3-6 Months">3-6 Months</option>
              <option value="Sometime this year">Sometime this year</option>
            </select>
            {formHelper.hasErrorDetails('decision')}
          </div>
        </div>
        <div className="py-[10px] flex items-center ">
          <input id="subscribe_to_newsletter" className="block mr-[5px] " type="checkbox"
            name="subscribe_to_newsletter" defaultChecked={true} />
          <label className={clsx(
            labelClassName,
            "block"
          )} htmlFor="subscribe_to_newsletter">
            Subscribe to newsletter
          </label>
        </div>
        <input type="hidden" name="_autopilot_list" value="91957542-4179-4C6D-839E-8F40E78E2164" />
        <input type="hidden" name="cf-turnstile-response" value={turnstileToken} />
        <div className="hs_submit">

          <div className="actions">
            {props.captchaEnabled &&
              <>
                <TurnstileWidget
                  ref={turnstileRef}
                  action="livedemo"
                  onTokenChange={(token) => {
                    setTurnstileToken(token);
                    if (token) {
                      setTurnstileError('');
                    }
                  }}
                />
                {turnstileError &&
                  <small className={clsx(
                    "text-[#a94442]",
                    "text-[12px]",
                    "pt-[5px]",
                    "block",
                  )}>{turnstileError}</small>
                }
                <br />
              </>
            }

            <AdbeatButton
              text={isPending ? "Submitting..." : "Submit"}
              type="submit"
              name='submitButton'
              disabled={isPending}
              className={clsx('bg-white',
                'bg-[#2da1f8]',
                'text-white',
                'hover:bg-[#1a8ce6]',
                'sm:w-[170px]',
                "h-[52px]",
                "min-w-[160px]",
                isPending && 'opacity-50 cursor-not-allowed',
              )} />
          </div>
        </div>
      </form>
    </div>
  );
}

export default EnterpriseForm;